'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import useAudioLevelStore from '@/store/useAudioLevelStore';
import {
  GeminiRealtimeWebSocket,
  type GeminiTranscriptionSource,
  type ProxyParams,
} from './GeminiRealtimeWebSocket';
import { pcmBufferToBase64 } from './audioUtils';
import {
  createDefaultRealtimeToolRegistry,
  type RealtimeToolRegistry,
  type RealtimeToolDefinition,
  type RealtimeToolContext,
} from './realtimeTools';
import { TranscriptionToolParser } from '@/components/whiteboard-v2/parseTranscriptionTools';
import type { TranscriptionToolHandler } from '@/components/whiteboard-v2/whiteboardV2LiveMode';
import posthog from 'posthog-js';
import { AudioRecorder } from './AudioRecorder';
import { uploadTurnSnapshot, uploadSessionEnd, sendSessionBeacon, uploadSessionStart } from './uploadLiveSession';
import useAuthStore from '@/store/useAuthStore';
import { normalizeLiveTutorSubject } from '@/lib/liveTutorSubject';

export type LiveModeStatus = 'inactive' | 'connecting' | 'active' | 'reconnecting';
type RnnoiseWorkletInstance = AudioWorkletNode & { destroy: () => void };
type ConversationEntry = {
  isUser: boolean;
  text: string;
  timestamp: string;
};

function resolveLiveRnnoiseEnabled(): boolean {
  return false;
}

// RNNoise tuning:
// - keep RNNoise always-on
// - reduce false-positive fallback (which can make denoise seem ineffective)
const DEFAULT_RNNOISE_LOW_LEVEL_THRESHOLD = 0.0008;
const DEFAULT_RNNOISE_LOW_LEVEL_BYPASS_MS = 5000;
const DEFAULT_RNNOISE_ENABLE_BROWSER_NS = false;
const DEFAULT_RNNOISE_ENABLE_BROWSER_AGC = false;

function parseEnvBoolean(value: string | undefined, fallback: boolean): boolean {
  if (value === undefined) return fallback;
  const normalized = value.trim().toLowerCase();
  if (normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on') return true;
  if (normalized === '0' || normalized === 'false' || normalized === 'no' || normalized === 'off') return false;
  return fallback;
}

function parseEnvNumber(
  value: string | undefined,
  fallback: number,
  options?: { min?: number; max?: number; integer?: boolean },
): number {
  if (value === undefined) return fallback;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;

  let result = parsed;
  if (options?.integer) {
    result = Math.round(result);
  }
  if (options?.min !== undefined) {
    result = Math.max(options.min, result);
  }
  if (options?.max !== undefined) {
    result = Math.min(options.max, result);
  }
  return result;
}

function resolveRnnoiseRuntimeTuning() {
  // Dev-only override entry; production always sticks to safe defaults.
  if (process.env.NODE_ENV !== 'development') {
    return {
      lowLevelThreshold: DEFAULT_RNNOISE_LOW_LEVEL_THRESHOLD,
      lowLevelBypassMs: DEFAULT_RNNOISE_LOW_LEVEL_BYPASS_MS,
      browserNoiseSuppression: DEFAULT_RNNOISE_ENABLE_BROWSER_NS,
      browserAutoGainControl: DEFAULT_RNNOISE_ENABLE_BROWSER_AGC,
      source: 'defaults(prod)',
    };
  }

  return {
    lowLevelThreshold: parseEnvNumber(
      process.env.NEXT_PUBLIC_LIVE_RNNOISE_LOW_LEVEL_THRESHOLD,
      DEFAULT_RNNOISE_LOW_LEVEL_THRESHOLD,
      { min: 0.0001, max: 0.05 },
    ),
    lowLevelBypassMs: parseEnvNumber(
      process.env.NEXT_PUBLIC_LIVE_RNNOISE_LOW_LEVEL_BYPASS_MS,
      DEFAULT_RNNOISE_LOW_LEVEL_BYPASS_MS,
      { min: 500, max: 20000, integer: true },
    ),
    browserNoiseSuppression: parseEnvBoolean(
      process.env.NEXT_PUBLIC_LIVE_RNNOISE_ENABLE_BROWSER_NS,
      DEFAULT_RNNOISE_ENABLE_BROWSER_NS,
    ),
    browserAutoGainControl: parseEnvBoolean(
      process.env.NEXT_PUBLIC_LIVE_RNNOISE_ENABLE_BROWSER_AGC,
      DEFAULT_RNNOISE_ENABLE_BROWSER_AGC,
    ),
    source: 'env(dev)',
  };
}

type RnnoiseRuntimeTuning = ReturnType<typeof resolveRnnoiseRuntimeTuning>;

const POSTHOG_REPLAY_RETRY_DELAYS_MS = [300, 800, 1500];
const PROACTIVE_SILENCE_NUDGE_DELAY_MS = 10000;
const PROACTIVE_SILENCE_NUDGE_COOLDOWN_MS = 12000;
const PROACTIVE_MAX_SILENCE_NUDGES_PER_QUESTION = 4;
const HISTORY_SILENCE_REENGAGE_PROMPTS = [
  '[System: The student is quiet right now. Do not wait passively. Re-engage with ONE short guided move: ask one concrete evidence-based micro-question tied to this question, add a tiny hint, and ask the student to explain or eliminate one option. Do not reveal the final answer.]',
  '[System: The student has gone silent. Please take initiative: ask one small history/social-studies question about chronology, evidence, or perspective, then invite the student to respond in one sentence. Keep it warm and concise, and do not give away the answer.]',
  '[System: The student has not responded yet. Continue proactive coaching with one scaffolded question that points to the key idea in the source/question, then ask the student to test one option with evidence. Keep it brief and avoid giving the final choice.]',
];
const SCIENCE_SILENCE_REENGAGE_PROMPTS = [
  '[System: The student is quiet right now. Do not wait passively. Re-engage with ONE short science move: ask one concrete evidence-based micro-question tied to variables, data, or mechanism, then add a tiny hint and one familiar everyday-life anchor. Do not reveal the final answer.]',
  '[System: The student has gone silent. Please take initiative: ask one small science question about evidence, cause-and-effect, or data trend, connect it to a simple daily-life example, then invite the student to respond in one sentence. Keep it warm and concise, and do not give away the answer.]',
  '[System: The student has not responded yet. Continue proactive coaching with one scaffolded science question that points to the key idea in the source/question, asks the student to test one option with evidence, and keeps language student-friendly. Keep it brief and avoid giving the final choice.]',
];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function getPostHogReplayUrl(): string | undefined {
  if (process.env.NODE_ENV === 'development') {
    return undefined;
  }

  try {
    return posthog.get_session_replay_url?.({ withTimestamp: true, timestampLookBack: 0 }) || undefined;
  } catch (error) {
    console.warn('[LiveMode] Failed to read PostHog replay URL:', error);
    return undefined;
  }
}

async function resolvePostHogReplayUrlWithRetry(
  fallbackUrl?: string,
  isSessionActive?: () => boolean,
): Promise<string | undefined> {
  let replayUrl = getPostHogReplayUrl() || fallbackUrl;
  if (replayUrl || process.env.NODE_ENV === 'development') {
    return replayUrl;
  }

  for (const delayMs of POSTHOG_REPLAY_RETRY_DELAYS_MS) {
    if (isSessionActive && !isSessionActive()) {
      return undefined;
    }

    await sleep(delayMs);

    if (isSessionActive && !isSessionActive()) {
      return undefined;
    }

    replayUrl = getPostHogReplayUrl();
    if (replayUrl) {
      return replayUrl;
    }
  }

  return fallbackUrl;
}

export interface WrongAnswerInfo {
  question: string;
  userAnswer: string;
  correctAnswer: string;
  explanation?: string;
  questionNumber?: number; // Original question number in the set (1-based)
  batchNumber?: number; // Diagnostic batch number (1-based), if available
}

// AnswerInfo includes both correct and incorrect answers
export interface AnswerInfo {
  question: string;
  userAnswer: string;
  correctAnswer: string;
  explanation?: string;
  questionNumber: number; // Original question number in the set (1-based)
  isCorrect: boolean;     // true = correct, false = wrong
}

export interface KeyPropertyInfo {
  title: string;
  content: string;
  examples?: string;
  explanation?: string;
}

export interface LessonReviewStepInfo {
  stepId: string;
  skillId: string;
  keyPropertyId: string;
  title: string;
  content: string;
  examples?: string;
  explanation?: string;
  picture?: string;
  picturePosition?: 'below' | 'right';
  practiceProblems?: Array<{
    id: string;
    question?: string;
    answer?: string;
    explanation?: string;
    options?: string[];
    type?: string;
  }>;
}

export interface ProblemContext {
  elementId: string;
  question: string;
  subject?: string;
  options?: string[];
  questTitle?: string;
  chapterTitle?: string;
  /** Correct answer — used for static JSON problems not in Supabase */
  answer?: string;
  /** Solution explanation — used for static JSON problems not in Supabase */
  explanation?: string;
  lessonTitle?: string;
  contextSummary?: string;
  // For review mode: explain wrong answers after practice
  wrongAnswers?: WrongAnswerInfo[];
  // For review mode: all answers (correct + wrong) for displaying full question list
  allAnswers?: AnswerInfo[];
  // For key property deep dive
  keyProperty?: KeyPropertyInfo;
  reviewPlan?: LessonReviewStepInfo[];
  currentStepIndex?: number;
  contextType?: 'question' | 'key_property' | 'mistake_review' | 'pet_companion_call' | 'learn_open_chat' | 'lesson_review';
  // Parent skill ID — used to disambiguate element IDs that are local per-skill
  skillId?: string;
}

export interface StableTranscriptionEntry {
  text: string;
  isUser: boolean;
  source: GeminiTranscriptionSource;
  sequence: number;
  sessionId: string;
}

export interface LiveModeSessionOptions {
  /** @deprecated No longer used — system prompt is constructed server-side by the WS proxy. */
  buildSystemPrompt?: (context: ProblemContext) => string;
  voiceName?: string;
  /** Personality ID for the tutor (e.g. 'ada', 'wizard'). Sent to the backend proxy. */
  personalityId?: string;
  /** Diagram mode hint for server-side prompt augmentation. */
  diagramMode?: 'auto' | 'coordinate_plane' | 'geometry';
  toolRegistry?: RealtimeToolRegistry;
  toolDefinitions?: RealtimeToolDefinition[];
  /** Tool handlers executed by parsing [TOOL_START]...[TOOL_END] markers from transcription (V2 whiteboard tools). */
  transcriptionToolDefinitions?: TranscriptionToolHandler[];
  toolContext?: Partial<RealtimeToolContext>;
  initialMessage?: string;
  initialTurnComplete?: boolean;
  onTranscription?: (text: string, isUser: boolean, source: GeminiTranscriptionSource) => void;
  onStableTranscription?: (entry: StableTranscriptionEntry) => void;
  /** Called on reconnect to get extra context (e.g. current board state) for the nudge message. */
  getReconnectContext?: () => string | undefined;
}

type ProactiveLiveTutorSubject = 'history' | 'science';

function getProactiveLiveTutorSubject(context: ProblemContext | null | undefined): ProactiveLiveTutorSubject | null {
  if (!context) return null;
  const contextType = context.contextType ?? 'question';
  const normalizedSubject = normalizeLiveTutorSubject(context.subject);
  if (contextType !== 'question') return null;
  if (normalizedSubject === 'history') return 'history';
  if (normalizedSubject === 'science') return 'science';
  return null;
}

// System prompt construction is now handled server-side by the WS proxy.
// See src/api/prompts/live_tutor.py for the prompt builders.

function formatLiveModeStartError(err: unknown): string {
  if (err instanceof DOMException) {
    if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
      return 'Microphone permission denied. Please allow microphone access in browser/system settings and try again.';
    }
    if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
      return 'No microphone detected. Please connect a microphone and try again.';
    }
    if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
      return 'Microphone is currently in use by another app. Close other apps using mic and try again.';
    }
    if (err.name === 'SecurityError') {
      return 'Microphone access requires a secure context (HTTPS or localhost).';
    }
  }

  if (err instanceof Error) {
    return err.message;
  }

  return 'Failed to start realtime session';
}

export function useLiveMode() {
  // Auth
  const user = useAuthStore((s) => s.user);
  const userRef = useRef(user);

  // Connection state
  const [status, setStatus] = useState<LiveModeStatus>('inactive');
  const [isTimeoutEnded, setIsTimeoutEnded] = useState(false);
  const [sessionWarningAt, setSessionWarningAt] = useState<number | null>(null);
  const [isModelSpeaking, setIsModelSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Audio levels live in a zustand store so updates only re-render waveform components,
  // not the entire page tree. Accessed via getState() to avoid subscribing this hook.
  const lastInputLevelTimeRef = useRef(0);
  const [totalTokens, setTotalTokens] = useState(0);
  const [promptTokens, setPromptTokens] = useState(0);
  const [outputTokens, setOutputTokens] = useState(0);

  // Context
  const [currentContext, setCurrentContext] = useState<ProblemContext | null>(null);

  // Refs for audio and WebSocket
  const geminiWsRef = useRef<GeminiRealtimeWebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
  const rnnoiseWorkletNodeRef = useRef<RnnoiseWorkletInstance | null>(null);
  const mediaSourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const isModelSpeakingRef = useRef(false);
  const sessionIdRef = useRef(0);
  const statusRef = useRef<LiveModeStatus>('inactive');
  const transcriptionLogBufferRef = useRef<Record<string, string>>({});
  const transcriptionLogTimerRef = useRef<Record<string, number | null>>({});
  const conversationHistoryRef = useRef<ConversationEntry[]>([]);
  const transcriptionSequenceRef = useRef(0);
  const currentSessionOptionsRef = useRef<LiveModeSessionOptions | undefined>(undefined);
  const liveSessionIdRef = useRef<string | null>(null);
  const reconnectCountRef = useRef(0);
  const rnnoiseLowLevelSinceRef = useRef<number | null>(null);
  const rnnoiseBypassedRef = useRef(false);
  const micInterruptGuardUntilRef = useRef<number>(0);
  const currentContextRef = useRef<ProblemContext | null>(null);
  const silenceNudgeTimerRef = useRef<number | null>(null);
  const sessionWallStartRef = useRef<number>(0);
  const durationTimerIdsRef = useRef<number[]>([]);
  const lastUserActivityAtRef = useRef<number>(0);
  const lastTutorActivityAtRef = useRef<number>(0);
  const lastSilenceNudgeAtRef = useRef<number>(0);
  const silenceNudgeCountRef = useRef<number>(0);

  // Transcription-based tool parsing refs (V2 whiteboard tools)
  const transcriptionParserRef = useRef<TranscriptionToolParser | null>(null);
  const transcriptionToolMapRef = useRef<Map<string, TranscriptionToolHandler['handler']> | null>(null);
  const transcriptionToolQueueRef = useRef<Promise<void>>(Promise.resolve());

  // Recording refs
  const recorderRef = useRef<AudioRecorder | null>(null);
  const beforeUnloadRef = useRef<(() => void) | null>(null);

  // Keep refs in sync with state
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  useEffect(() => {
    isModelSpeakingRef.current = isModelSpeaking;
  }, [isModelSpeaking]);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    currentContextRef.current = currentContext;
  }, [currentContext]);

  const clearSilenceNudgeTimer = useCallback(() => {
    if (silenceNudgeTimerRef.current !== null) {
      window.clearTimeout(silenceNudgeTimerRef.current);
      silenceNudgeTimerRef.current = null;
    }
  }, []);

  const scheduleProactiveSilenceNudge = useCallback((sessionId: number, delayMs: number = PROACTIVE_SILENCE_NUDGE_DELAY_MS) => {
    clearSilenceNudgeTimer();

    const runSilenceCheck = () => {
      if (sessionIdRef.current !== sessionId || statusRef.current !== 'active') return;
      const proactiveSubject = getProactiveLiveTutorSubject(currentContextRef.current);
      if (!proactiveSubject) return;
      if (!geminiWsRef.current) return;

      if (isModelSpeakingRef.current) {
        silenceNudgeTimerRef.current = window.setTimeout(runSilenceCheck, 1200);
        return;
      }

      if (silenceNudgeCountRef.current >= PROACTIVE_MAX_SILENCE_NUDGES_PER_QUESTION) return;

      const now = Date.now();
      if (lastSilenceNudgeAtRef.current > 0) {
        const cooldownRemaining = PROACTIVE_SILENCE_NUDGE_COOLDOWN_MS - (now - lastSilenceNudgeAtRef.current);
        if (cooldownRemaining > 0) {
          silenceNudgeTimerRef.current = window.setTimeout(runSilenceCheck, cooldownRemaining);
          return;
        }
      }

      const lastTutorAt = lastTutorActivityAtRef.current;
      const lastUserAt = lastUserActivityAtRef.current;
      if (lastTutorAt <= 0 || lastUserAt >= lastTutorAt) return;

      const silenceForMs = now - Math.max(lastTutorAt, lastUserAt);
      if (silenceForMs < PROACTIVE_SILENCE_NUDGE_DELAY_MS) {
        silenceNudgeTimerRef.current = window.setTimeout(
          runSilenceCheck,
          PROACTIVE_SILENCE_NUDGE_DELAY_MS - silenceForMs,
        );
        return;
      }

      const promptPool = proactiveSubject === 'science'
        ? SCIENCE_SILENCE_REENGAGE_PROMPTS
        : HISTORY_SILENCE_REENGAGE_PROMPTS;
      const nudgePrompt = promptPool[silenceNudgeCountRef.current % promptPool.length];
      geminiWsRef.current.sendRealtimeText(nudgePrompt);
      silenceNudgeCountRef.current += 1;
      lastSilenceNudgeAtRef.current = now;
      lastTutorActivityAtRef.current = now;
      silenceNudgeTimerRef.current = window.setTimeout(runSilenceCheck, PROACTIVE_SILENCE_NUDGE_DELAY_MS);
    };

    silenceNudgeTimerRef.current = window.setTimeout(runSilenceCheck, Math.max(400, delayMs));
  }, [clearSilenceNudgeTimer]);

  const flushTranscriptionLog = useCallback((isUser: boolean, source: GeminiTranscriptionSource) => {
    const roleKey = isUser ? 'user' : 'tutor';
    const bucketKey = `${roleKey}:${source}`;
    const pending = transcriptionLogBufferRef.current[bucketKey]?.trim();
    if (!pending) return;

    const displayText = source === 'model_turn_text'
      ? `[MODEL_TURN_TEXT] ${pending}`
      : pending;
    console.log(`[LiveMode] Transcription (${roleKey}):`, displayText);
    transcriptionLogBufferRef.current[bucketKey] = '';
    transcriptionLogTimerRef.current[bucketKey] = null;

    if ((source === 'input_transcription' || source === 'output_transcription') && liveSessionIdRef.current) {
      transcriptionSequenceRef.current += 1;
      currentSessionOptionsRef.current?.onStableTranscription?.({
        text: pending,
        isUser,
        source,
        sequence: transcriptionSequenceRef.current,
        sessionId: liveSessionIdRef.current,
      });
    }
  }, []);

  const enqueueTranscriptionLog = useCallback((text: string, isUser: boolean, source: GeminiTranscriptionSource) => {
    const normalized = text.trim();
    if (!normalized) return;

    const roleKey = isUser ? 'user' : 'tutor';
    const bucketKey = `${roleKey}:${source}`;
    const existing = transcriptionLogBufferRef.current[bucketKey];
    transcriptionLogBufferRef.current[bucketKey] = existing ? `${existing} ${normalized}` : normalized;

    const existingTimer = transcriptionLogTimerRef.current[bucketKey];
    if (existingTimer !== null) {
      window.clearTimeout(existingTimer);
    }

    transcriptionLogTimerRef.current[bucketKey] = window.setTimeout(() => {
      flushTranscriptionLog(isUser, source);
    }, 700);
  }, [flushTranscriptionLog]);

  const appendConversationHistory = useCallback((text: string, isUser: boolean) => {
    const normalized = text.trim();
    if (!normalized) return;

    const history = conversationHistoryRef.current;
    const last = history[history.length - 1];

    if (last && last.isUser === isUser) {
      last.text = `${last.text} ${normalized}`.trim();
      last.timestamp = new Date().toISOString();
      return;
    }

    history.push({
      isUser,
      text: normalized,
      timestamp: new Date().toISOString(),
    });
  }, []);

  const setupAudioProcessing = useCallback(async (rnnoiseEnabled: boolean, rnnoiseTuning: RnnoiseRuntimeTuning) => {
    const ctx = audioContextRef.current;
    const stream = streamRef.current;

    if (!ctx || !stream) {
      console.error('[LiveMode] AudioContext or stream not available');
      return;
    }

    try {
      // Resume AudioContext if suspended (browser policy)
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }

      // Load PCM export worklet module (required)
      await ctx.audioWorklet.addModule('/worklets/gemini-audio-processor.js');

      // Create AudioWorkletNode
      audioWorkletNodeRef.current = new AudioWorkletNode(ctx, 'gemini-audio-processor', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        channelCount: 1,
        channelCountMode: 'explicit',
        channelInterpretation: 'speakers',
        processorOptions: {
          inputSampleRate: ctx.sampleRate,
          targetSampleRate: 16000,
          outputFrameSize: 1600,
        },
      });

      // Handle processed audio data from worklet
      audioWorkletNodeRef.current.port.onmessage = (event) => {
        if (statusRef.current !== 'active') {
          return;
        }

        const { pcmData, level } = event.data;
        if (rnnoiseEnabled && !rnnoiseBypassedRef.current) {
          const now = performance.now();
          const isLowLevel = level < rnnoiseTuning.lowLevelThreshold;
          if (isLowLevel) {
            if (rnnoiseLowLevelSinceRef.current === null) {
              rnnoiseLowLevelSinceRef.current = now;
            } else if (
              now - rnnoiseLowLevelSinceRef.current > rnnoiseTuning.lowLevelBypassMs &&
              mediaSourceNodeRef.current &&
              audioWorkletNodeRef.current
            ) {
              console.warn('[LiveMode] RNNoise output stayed near-silent; bypassing RNNoise to raw mic');
              rnnoiseBypassedRef.current = true;
              try {
                mediaSourceNodeRef.current.disconnect();
              } catch {
                // ignore disconnect edge cases
              }
              try {
                rnnoiseWorkletNodeRef.current?.disconnect();
              } catch {
                // ignore disconnect edge cases
              }
              mediaSourceNodeRef.current.connect(audioWorkletNodeRef.current);
            }
          } else {
            rnnoiseLowLevelSinceRef.current = null;
          }
        }

        // Throttle visual level updates to ~15fps
        const now = performance.now();
        if (now - lastInputLevelTimeRef.current >= 66) {
          lastInputLevelTimeRef.current = now;
          useAudioLevelStore.getState().setInputLevel(level);
        }

        const nowForGuard = performance.now();
        const isInMicGuard = nowForGuard < micInterruptGuardUntilRef.current;

        // Capture user audio for recording (skip during model speaking to avoid echo)
        // and skip entirely during the initial mic guard window.
        if (!isInMicGuard && !isModelSpeakingRef.current) {
          recorderRef.current?.appendUserAudio(new Int16Array(pcmData));
        }

        // Always send audio to Gemini — even while model is speaking —
        // except during the initial mic guard window to avoid instant barge-in.
        if (!isInMicGuard && geminiWsRef.current) {
          const base64Data = pcmBufferToBase64(pcmData);
          geminiWsRef.current.sendAudioChunk(base64Data);
        }
      };

      // Connect media stream to worklet
      const source = ctx.createMediaStreamSource(stream);
      mediaSourceNodeRef.current = source;

      if (rnnoiseEnabled) {
        try {
          await ctx.audioWorklet.addModule('/worklets/rnnoiseWorklet.js');
          const rnnoiseModule = await import('@sapphi-red/web-noise-suppressor');
          const rnnoiseWasmBinary = await rnnoiseModule.loadRnnoise({
            url: '/worklets/rnnoise.wasm',
            simdUrl: '/worklets/rnnoise_simd.wasm',
          });
          rnnoiseWorkletNodeRef.current = new rnnoiseModule.RnnoiseWorkletNode(ctx, {
            wasmBinary: rnnoiseWasmBinary,
            maxChannels: 1,
          }) as RnnoiseWorkletInstance;

          source.connect(rnnoiseWorkletNodeRef.current);
          rnnoiseWorkletNodeRef.current.connect(audioWorkletNodeRef.current);
          console.log('[LiveMode] RNNoise enabled for microphone input');
        } catch (rnnoiseErr) {
          console.warn('[LiveMode] RNNoise unavailable, falling back to raw mic input:', rnnoiseErr);
          source.connect(audioWorkletNodeRef.current);
        }
      } else {
        console.log('[LiveMode] RNNoise disabled by feature flag/env configuration');
        source.connect(audioWorkletNodeRef.current);
      }

      // console.log('[LiveMode] Audio processing connected');
    } catch (err) {
      console.error('[LiveMode] Error setting up audio processing:', err);
      setError('Failed to setup audio processing');
    }
  }, []);

  const cleanupSessionResources = useCallback((options?: { disconnectSocket?: boolean }) => {
    sessionIdRef.current += 1;
    clearSilenceNudgeTimer();
    lastUserActivityAtRef.current = 0;
    lastTutorActivityAtRef.current = 0;
    lastSilenceNudgeAtRef.current = 0;
    silenceNudgeCountRef.current = 0;

    for (const [key, timer] of Object.entries(transcriptionLogTimerRef.current)) {
      if (timer !== null) {
        window.clearTimeout(timer);
      }
      transcriptionLogTimerRef.current[key] = null;
    }
    flushTranscriptionLog(true, 'input_transcription');
    flushTranscriptionLog(false, 'model_turn_text');
    flushTranscriptionLog(false, 'output_transcription');

    // Flush and clear transcription tool parser
    if (transcriptionParserRef.current) {
      transcriptionParserRef.current.flush();
      transcriptionParserRef.current = null;
    }
    transcriptionToolMapRef.current = null;
    transcriptionToolQueueRef.current = Promise.resolve();

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (mediaSourceNodeRef.current) {
      mediaSourceNodeRef.current.disconnect();
      mediaSourceNodeRef.current = null;
    }

    if (rnnoiseWorkletNodeRef.current) {
      rnnoiseWorkletNodeRef.current.disconnect();
      rnnoiseWorkletNodeRef.current.destroy();
      rnnoiseWorkletNodeRef.current = null;
    }
    if (audioWorkletNodeRef.current) {
      audioWorkletNodeRef.current.port.onmessage = null;
      audioWorkletNodeRef.current.disconnect();
      audioWorkletNodeRef.current = null;
    }

    if (audioContextRef.current) {
      void audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    if (options?.disconnectSocket !== false && geminiWsRef.current) {
      geminiWsRef.current.disconnect();
    }
    geminiWsRef.current = null;

    // Cleanup recording
    if (beforeUnloadRef.current) {
      window.removeEventListener('beforeunload', beforeUnloadRef.current);
      beforeUnloadRef.current = null;
    }
    recorderRef.current = null;
    currentSessionOptionsRef.current = undefined;
    liveSessionIdRef.current = null;
    transcriptionSequenceRef.current = 0;

    setStatus('inactive');
    setIsModelSpeaking(false);
    useAudioLevelStore.getState().reset();
    setTotalTokens(0);
    setPromptTokens(0);
    setOutputTokens(0);
    setCurrentContext(null);
  }, [clearSilenceNudgeTimer, flushTranscriptionLog]);

  const endSession = useCallback((reason: string = 'unspecified') => {
    console.log('[LiveMode] Ending session...', { reason, sessionId: liveSessionIdRef.current });
    // Invalidate any in-flight async startSession work so a modal that closes
    // during startup cannot "resurrect" a session a moment later.
    sessionIdRef.current += 1;

    // Finalize recording and upload — finalize() returns a self-contained snapshot
    // (Blob + plain data), so it's safe to cleanup resources immediately after.
    if (recorderRef.current) {
      const finalData = recorderRef.current.finalize();
      uploadSessionEnd(finalData).catch((err) =>
        console.error('[LiveMode] Failed to upload session end:', err),
      );
    }

    durationTimerIdsRef.current.forEach(window.clearTimeout);
    durationTimerIdsRef.current = [];
    sessionWallStartRef.current = 0;
    cleanupSessionResources({ disconnectSocket: true });
    conversationHistoryRef.current = [];
  }, [cleanupSessionResources]);

  const startSession = useCallback(async (context: ProblemContext, options?: LiveModeSessionOptions): Promise<boolean> => {
    try {
      const sessionId = sessionIdRef.current + 1;
      const rnnoiseEnabled = resolveLiveRnnoiseEnabled();
      const rnnoiseTuning = resolveRnnoiseRuntimeTuning();
      reconnectCountRef.current = 0;
      sessionWallStartRef.current = Date.now();
      durationTimerIdsRef.current.forEach(window.clearTimeout);
      durationTimerIdsRef.current = [];
      setIsTimeoutEnded(false);
      setSessionWarningAt(null);
      rnnoiseBypassedRef.current = false;
      rnnoiseLowLevelSinceRef.current = null;
      clearSilenceNudgeTimer();
      lastUserActivityAtRef.current = 0;
      lastTutorActivityAtRef.current = 0;
      lastSilenceNudgeAtRef.current = 0;
      silenceNudgeCountRef.current = 0;
      sessionIdRef.current = sessionId;
      setError(null);
      setStatus('connecting');
      setCurrentContext(context);
      currentSessionOptionsRef.current = options;
      console.log('[LiveMode] RNNoise config for this session:', {
        enabled: rnnoiseEnabled,
        source: `hardcoded-default-on + ${rnnoiseTuning.source}`,
        lowLevelThreshold: rnnoiseTuning.lowLevelThreshold,
        lowLevelBypassMs: rnnoiseTuning.lowLevelBypassMs,
        browserNoiseSuppression: rnnoiseTuning.browserNoiseSuppression,
        browserAutoGainControl: rnnoiseTuning.browserAutoGainControl,
      });

      if (!window.isSecureContext && window.location.hostname !== 'localhost') {
        throw new Error('Microphone access requires HTTPS (or localhost).');
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('This browser does not support microphone capture (getUserMedia).');
      }

      // Pre-check permission state when available so we can surface a clear error before prompting.
      try {
        if (navigator.permissions?.query) {
          const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          if (result.state === 'denied') {
            throw new Error('Microphone permission is blocked. Please enable it in browser settings and try again.');
          }
        }
      } catch (permissionErr) {
        // Ignore unsupported Permissions API errors; getUserMedia will provide the canonical failure.
        if (permissionErr instanceof Error && permissionErr.message.includes('blocked')) {
          throw permissionErr;
        }
      }

      // Request microphone access with realtime voice settings
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: rnnoiseEnabled ? 48000 : 16000,
          channelCount: 1,
          echoCancellation: true,
          // Keep browser AGC/NS off when RNNoise is active to avoid double-processing.
          autoGainControl: rnnoiseEnabled ? rnnoiseTuning.browserAutoGainControl : true,
          noiseSuppression: rnnoiseEnabled ? rnnoiseTuning.browserNoiseSuppression : true,
        },
      });
      if (sessionIdRef.current !== sessionId) {
        stream.getTracks().forEach((track) => track.stop());
        return false;
      }
      streamRef.current = stream;
      // console.log('[LiveMode] Microphone access granted');

      // Use 48kHz when RNNoise is enabled; otherwise keep 16kHz path.
      audioContextRef.current = new AudioContext({ sampleRate: rnnoiseEnabled ? 48000 : 16000 });
      if (sessionIdRef.current !== sessionId) {
        void audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
        stream.getTracks().forEach((track) => track.stop());
        return false;
      }

      // Generate session ID early — needed by both proxyParams and AudioRecorder
      const liveSessionId = crypto.randomUUID();
      liveSessionIdRef.current = liveSessionId;
      // Capture PostHog replay URL with timestamp offset so Langfuse links jump
      // directly to the moment the tutor session started within the recording.
      const initialPosthogRecordingUrl = getPostHogReplayUrl();
      console.log('[LiveMode] Session ID:', liveSessionId, 'Initial PostHog replay:', initialPosthogRecordingUrl);

      // Build proxy params for the backend WS proxy (system prompt is constructed server-side)
      const proxyParams: ProxyParams = {
        context_type: context.contextType || 'question',
        user_id: userRef.current?.id,
        element_id: context.elementId,
        skill_id: context.skillId,
        subject: normalizeLiveTutorSubject(context.subject),
        quest_title: context.questTitle,
        chapter_title: context.chapterTitle,
        lesson_title: context.lessonTitle,
        context_summary: context.contextSummary,
        voice_name: options?.voiceName,
        personality_id: options?.personalityId,
        diagram_mode: options?.diagramMode,
        question: context.question || undefined,
        session_id: liveSessionId,
      };
      // For mistake review, send wrong answers as JSON
      if (context.wrongAnswers && context.wrongAnswers.length > 0) {
        proxyParams.wrong_answers = JSON.stringify(context.wrongAnswers);
      }
      if (context.reviewPlan && context.reviewPlan.length > 0) {
        proxyParams.review_plan = JSON.stringify(context.reviewPlan);
      }
      if (typeof context.currentStepIndex === 'number') {
        proxyParams.current_step_index = context.currentStepIndex;
      }
      // Send displayed options (shuffled order shown to student) so the backend uses the same order
      if (context.options && context.options.length > 0) {
        proxyParams.options = JSON.stringify(context.options);
      }
      // Pass answer/explanation for static JSON problems not in Supabase
      if (context.answer) proxyParams.answer = context.answer;
      if (context.explanation) proxyParams.explanation = context.explanation;
      // V1 backward compat: if caller provides buildSystemPrompt, pass the result to the proxy
      if (options?.buildSystemPrompt) {
        proxyParams.client_prompt = options.buildSystemPrompt(context);
      }

      // Initialize AudioRecorder for this session
      const sessionStartTime = Date.now();
      const userId = userRef.current?.id;
      const userEmail = userRef.current?.email;
      recorderRef.current = new AudioRecorder(liveSessionId, {
        userId,
        userEmail,
        startTime: sessionStartTime,
        onTurnSnapshot: (snapshot) => {
          uploadTurnSnapshot(liveSessionId, userId, sessionStartTime, snapshot, {
            rnnoiseEnabled,
            reconnectCount: reconnectCountRef.current,
            micSampleRate: audioContextRef.current?.sampleRate,
          }).catch((err) =>
            console.error('[LiveMode] Turn snapshot upload failed:', err),
          );
        },
      });
      if (sessionIdRef.current !== sessionId) {
        return false;
      }

      // Register beforeunload handler for abnormal exit
      const onBeforeUnload = () => sendSessionBeacon(liveSessionId, userId, sessionStartTime);
      window.addEventListener('beforeunload', onBeforeUnload);
      beforeUnloadRef.current = onBeforeUnload;

      // Initialize WebSocket with callbacks
      setTotalTokens(0);
      setPromptTokens(0);
      setOutputTokens(0);
      conversationHistoryRef.current = [];
      transcriptionSequenceRef.current = 0;

      const toolContext: RealtimeToolContext = {
        endSession: () => endSession('tool_context'),
        ...(options?.toolContext ?? {}),
      };
      const toolRegistry = options?.toolRegistry
        ? options.toolRegistry
        : createDefaultRealtimeToolRegistry(toolContext, options?.toolDefinitions);

      // Set up transcription tool parser (V2: tool calls embedded in speech as markers)
      if (options?.transcriptionToolDefinitions?.length) {
        transcriptionParserRef.current = new TranscriptionToolParser();
        transcriptionToolMapRef.current = new Map(
          options.transcriptionToolDefinitions.map(t => [t.name, t.handler]),
        );
        transcriptionToolQueueRef.current = Promise.resolve();
      }

      geminiWsRef.current = new GeminiRealtimeWebSocket({
        proxyParams,
        voiceName: options?.voiceName,
        toolRegistry,
        toolContext,
        callbacks: {
          onSetupComplete: () => {
            if (sessionIdRef.current !== sessionId) return;
            setStatus('active');
            micInterruptGuardUntilRef.current = performance.now() + 3000;
            setupAudioProcessing(rnnoiseEnabled, rnnoiseTuning);

            // Capture provider info from proxy for Langfuse metadata
            if (recorderRef.current && geminiWsRef.current?.provider) {
              recorderRef.current.provider = geminiWsRef.current.provider;
            }

            void (async () => {
              const posthogRecordingUrl = await resolvePostHogReplayUrlWithRetry(
                initialPosthogRecordingUrl,
                () => sessionIdRef.current === sessionId,
              );

              if (sessionIdRef.current !== sessionId) return;

              if (recorderRef.current && posthogRecordingUrl) {
                recorderRef.current.posthogRecordingUrl = posthogRecordingUrl;
              }

              uploadSessionStart({
                sessionId: liveSessionId,
                userId,
                userEmail,
                startTime: sessionStartTime,
                provider: geminiWsRef.current?.provider,
                posthogRecordingUrl,
                runtimeMeta: {
                  rnnoiseEnabled,
                  reconnectCount: reconnectCountRef.current,
                  micSampleRate: audioContextRef.current?.sampleRate,
                },
                contextType: proxyParams.context_type,
                elementId: proxyParams.element_id,
                skillId: proxyParams.skill_id,
              }).catch((err) => console.error('[LiveMode] Session start upload failed:', err));
            })();

            // Send initial greeting prompt to make tutor start speaking
            const isReviewMode = (context.contextType === 'mistake_review' || context.wrongAnswers) && context.wrongAnswers && context.wrongAnswers.length > 0;
            const isKeyProperty = context.contextType === 'key_property';
            const isLessonReview = context.contextType === 'lesson_review';
            const proactiveSubject = getProactiveLiveTutorSubject(context);
            const defaultInitialMessage = isReviewMode
              ? "Can you help me understand the questions I got wrong?"
              : isKeyProperty
                ? "I want to deeply understand this key property. Can you walk me through it?"
                : isLessonReview
                  ? "I want to review this lesson. Start with the first key property, ask me to explain it first, and guide me through the examples one at a time."
                : proactiveSubject === 'history'
                  ? "Please take the Lead Learner role: proactively analyze this history/social studies problem and ask one guiding background or evidence question."
                  : proactiveSubject === 'science'
                    ? "Please take the Lead Learner role: proactively analyze this science problem and ask one guiding phenomenon or evidence question."
                  : "Hello! I'm ready to work on this problem. Can you help guide me through it?";
            const initialMessage = options?.initialMessage ?? defaultInitialMessage;
            const initialTurnComplete = options?.initialTurnComplete ?? true;
            geminiWsRef.current?.sendRealtimeText(initialMessage);
          },
          onPlayingStateChange: (playing) => {
            if (sessionIdRef.current !== sessionId) return;
            setIsModelSpeaking(playing);
            if (playing) {
              clearSilenceNudgeTimer();
            } else {
              scheduleProactiveSilenceNudge(sessionId);
            }
          },
          onOutputAudioLevel: (level) => {
            if (sessionIdRef.current !== sessionId) return;
            useAudioLevelStore.getState().setOutputLevel(level);
          },
          onTranscription: (text, isUser, source) => {
            if (sessionIdRef.current !== sessionId) return;
            const now = Date.now();

            if (isUser && source === 'input_transcription') {
              lastUserActivityAtRef.current = now;
              silenceNudgeCountRef.current = 0;
              clearSilenceNudgeTimer();
            } else if (!isUser && (source === 'output_transcription' || source === 'model_turn_text')) {
              lastTutorActivityAtRef.current = now;
            }

            let displayText = text;

            // Parse transcription tool markers for non-user text
            if (!isUser && transcriptionParserRef.current && transcriptionToolMapRef.current) {
              const { cleanText, toolCalls } = transcriptionParserRef.current.push(text);
              displayText = cleanText;

              for (const call of toolCalls) {
                call.name = call.name.toLowerCase();
                const handler = transcriptionToolMapRef.current.get(call.name);
                if (handler) {
                  transcriptionToolQueueRef.current = transcriptionToolQueueRef.current.then(async () => {
                    try {
                      const result = await handler(call.args);
                      console.log(`[LiveMode] TranscriptionTool: ${call.name}`, call.args, result?.response);
                      recorderRef.current?.appendToolCall(
                        call.name,
                        (call.args ?? {}) as Record<string, unknown>,
                        result?.response ?? { result: 'ok' },
                      );
                    } catch (err) {
                      console.error(`[LiveMode] TranscriptionTool error: ${call.name}`, err);
                    }
                  });
                } else {
                  console.warn(`[LiveMode] Unknown transcription tool: ${call.name}`, call);
                }
              }
            }

            // Log and record raw text (preserving markers for Langfuse);
            // only use displayText for conversation history UI
            enqueueTranscriptionLog(text, isUser, source);
            if (source === 'input_transcription' || source === 'output_transcription') {
              appendConversationHistory(displayText, isUser);
            }
            // Feed transcription to recorder
            if (source === 'input_transcription') {
              recorderRef.current?.appendUserTranscript(text);
            } else if (source === 'output_transcription') {
              recorderRef.current?.appendModelTranscript(text);
            }
            if (!isUser && (source === 'output_transcription' || source === 'model_turn_text')) {
              scheduleProactiveSilenceNudge(sessionId);
            }
            options?.onTranscription?.(text, isUser, source);
          },
          onUsageUpdate: (usage) => {
            if (sessionIdRef.current !== sessionId) return;
            setTotalTokens(usage.totalTokens);
            setPromptTokens(usage.promptTokensTotal);
            setOutputTokens(usage.outputTokensTotal);
          },
          onError: (err) => {
            if (sessionIdRef.current !== sessionId) return;
            console.error('[LiveMode] WebSocket error:', err);
            setError(err.message);
            cleanupSessionResources({ disconnectSocket: true });
          },
          onDisconnect: () => {
            if (sessionIdRef.current !== sessionId) return;
            cleanupSessionResources({ disconnectSocket: false });
          },
          onModelAudioChunk: (float32Data) => {
            if (sessionIdRef.current !== sessionId) return;
            recorderRef.current?.appendModelAudio(float32Data);
          },
          onTurnComplete: () => {
            if (sessionIdRef.current !== sessionId) return;
            recorderRef.current?.onTurnComplete();
            lastTutorActivityAtRef.current = Date.now();
            scheduleProactiveSilenceNudge(sessionId);
          },
          onGenerationComplete: () => {
            if (sessionIdRef.current !== sessionId) return;
            recorderRef.current?.onGenerationComplete();
          },
          onInterrupted: () => {
            if (sessionIdRef.current !== sessionId) return;
            // Flush partial tool markers — the model was cut off mid-stream
            // so any incomplete [TOOL_START]...[TOOL_END] will never finish
            transcriptionParserRef.current?.flush();
          },
          onToolCall: (name, args, result) => {
            if (sessionIdRef.current !== sessionId) return;
            recorderRef.current?.appendToolCall(name, args, result);
          },
          onReconnecting: ({ code, reason }) => {
            if (sessionIdRef.current !== sessionId) return;
            reconnectCountRef.current += 1;
            console.log('[LiveMode] Reconnecting...', { code, reason });
            setStatus('reconnecting');
            recorderRef.current?.appendSystemEvent(`disconnected (code=${code}${reason ? `, reason=${reason}` : ''})`);
          },
          onReconnected: () => {
            if (sessionIdRef.current !== sessionId) return;
            console.log('[LiveMode] Reconnected');
            setStatus('active');
            recorderRef.current?.appendSystemEvent('reconnected');
            // Build recent conversation context so the model knows where it left off
            const recentHistory = conversationHistoryRef.current.slice(-4).map(
              (e) => `${e.isUser ? 'Student' : 'Tutor'}: ${e.text}`
            ).join('\n');

            const reconnectNudges = [
              'Oh whoops, I think we lost each other for a second there. Anyway, where were we...',
              'Hey sorry about that, the connection got a little wonky. OK so...',
              'Ooh looks like we got cut off for a moment. No worries, I\'m back!',
              'Ah there we go, sorry the connection dropped for a sec. Alright so...',
              'Oh hey, we\'re back! Sorry about that little hiccup. OK let me pick up where I was...',
            ];
            const nudge = reconnectNudges[Math.floor(Math.random() * reconnectNudges.length)];

            const contextBlock = recentHistory
              ? `\n\nHere is what was said right before the disconnection:\n${recentHistory}\n\nContinue from AFTER this point — do NOT repeat questions already asked or re-explain things the student already answered correctly.`
              : '';

            const boardContext = options?.getReconnectContext?.() ?? '';
            const boardBlock = boardContext ? `\n\n${boardContext}` : '';

            geminiWsRef.current?.sendRealtimeText(
              `[System: The connection was briefly unstable and just recovered. Say something casual like: "${nudge}" — but rephrase it in your own voice, keep it short and natural, then continue teaching from where you left off.${contextBlock}${boardBlock}]`,
            );
            lastTutorActivityAtRef.current = Date.now();
            scheduleProactiveSilenceNudge(sessionId);
          },
        },
      });

      // Connect (fetches token first)
      await geminiWsRef.current.connect();
      if (sessionIdRef.current !== sessionId) {
        geminiWsRef.current.disconnect();
        geminiWsRef.current = null;
        return false;
      }

      // ─── session duration limit ────────────────────────────────────────────
      const SESSION_WARNING_MS = 13 * 60 * 1000;
      const SESSION_END_MS = 15 * 60 * 1000;
      const elapsed = Date.now() - sessionWallStartRef.current;

      const warnId = window.setTimeout(() => {
        setSessionWarningAt(Date.now());
        geminiWsRef.current?.sendRealtimeText(
          `[System: You have been tutoring for about 13 minutes. In your own natural voice, briefly mention something like: "Hey, just so you know — if we chat too long it can affect signal quality, so we've got about 2 minutes left. Let's make the most of it!" Keep it warm and brief, then continue teaching normally.]`,
        );
      }, SESSION_WARNING_MS - elapsed);

      const endId = window.setTimeout(() => {
        endSession();
        setIsTimeoutEnded(true);
      }, SESSION_END_MS - elapsed);

      durationTimerIdsRef.current = [warnId, endId];

      return true;
    } catch (err) {
      console.error('[LiveMode] Error starting session:', err);
      setError(formatLiveModeStartError(err));
      endSession('startSession_error');
      return false;
    }
  }, [
    appendConversationHistory,
    clearSilenceNudgeTimer,
    endSession,
    enqueueTranscriptionLog,
    setupAudioProcessing,
    cleanupSessionResources,
    scheduleProactiveSilenceNudge,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      endSession('hook_unmount');
    };
  }, [endSession]);

  const sendClientContent = useCallback((text: string, _turnComplete: boolean = false) => {
    // In Gemini 3.1, client_content is restricted to initial history seeding.
    // Mid-session text is sent via realtime_input instead.
    geminiWsRef.current?.sendRealtimeText(text);
  }, []);

  // Update context with new question (e.g., when moving to next question in a set)
  const updateQuestionContext = useCallback((context: ProblemContext) => {
    if (status !== 'active') return;

    setCurrentContext(context);
    clearSilenceNudgeTimer();
    silenceNudgeCountRef.current = 0;
    lastUserActivityAtRef.current = Date.now();
    lastTutorActivityAtRef.current = 0;
    lastSilenceNudgeAtRef.current = 0;

    // Build a message to inform the model about the new question
    const optionsText = context.options
      ? `\nOptions:\n${context.options.map((o, i) => `${i + 1}. ${o}`).join('\n')}`
      : '';

    const contextMessage = `[New question] The student is now working on the next PRIMARY whiteboard problem:\n\n${context.question}${optionsText}\n\nThis is the main session problem, so guide it with show_step and normal tutoring flow. Do NOT call show_note just to restate this primary problem.\nOnly call show_note with title=\"New Problem\" and kind=\"question\" if you introduce an EXTRA side problem that is not represented in the whiteboard steps.`;

    // Send to model
    geminiWsRef.current?.sendRealtimeText(contextMessage);
    console.log('[LiveMode] Sent new question context to model');
  }, [clearSilenceNudgeTimer, status]);

  return {
    status,
    isModelSpeaking,
    totalTokens,
    promptTokens,
    outputTokens,
    error,
    currentContext,
    startSession,
    endSession,
    sendClientContent,
    updateQuestionContext,
    isTimeoutEnded,
    sessionWarningAt,
    resetTimeoutEnd: () => { setIsTimeoutEnded(false); setSessionWarningAt(null); },
  };
}
